import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import StatusBadge from '../components/StatusBadge';

const WEEKDAYS_KR = ['일', '월', '화', '수', '목', '금', '토'];
const pad2 = n => String(n).padStart(2, '0');
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const formatNum = n => (n || 0).toLocaleString();
const formatDateKR = () => {
  const d = new Date();
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${WEEKDAYS_KR[d.getDay()]})`;
};
// "2026-08-10 14:20:00" 같은 문자열에서 "08/10"만 뽑아 카드 안 목록에 짧게 표시
const shortDate = (s) => (s || '').slice(5, 10).replace('-', '/');
const minutesAgo = (iso) => {
  if (!iso) return '';
  // DB created_at은 타임존 표시가 없으면 실제로는 UTC로 저장되어 있다(서버가 Windows에서
  // SQLite datetime('now','localtime')을 쓰는데 CRT가 TZ=Asia/Seoul을 인식하지 못하는 문제).
  // 표시 없는 문자열을 KST로 오인해 파싱하면 약 9시간 어긋나므로 UTC로 명시해서 파싱한다.
  let s = String(iso).replace(' ', 'T');
  if (!/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const diff = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 60000));
  if (diff < 1) return '방금 전';
  if (diff < 60) return `${diff}분 전`;
  return `${Math.round(diff / 60)}시간 전`;
};

const ROW_COUNT = 5; // 카드마다 항상 5줄 슬롯을 확보해 목록 개수와 무관하게 카드 높이를 고정한다

// ── 상단 카드 하나 — 각진(모서리 컷) 리본형 헤더 + 고정 5줄 리스트, 카드 헤더는 해당 메뉴 바로가기,
//    각 줄(항목)은 클릭하면 그 내용의 상세 모달이 뜬다(onItemClick) ──
function DashCard({ to, icon, title, color, loading, items, empty, onItemClick }) {
  const rows = Array.from({ length: ROW_COUNT }, (_, i) => (items ? items[i] : undefined));
  return (
    <div
      style={{
        borderRadius: 16, overflow: 'hidden', height: '100%', boxSizing: 'border-box',
        // 카드 우측 상단 모서리를 살짝 대각선으로 잘라내어 뱃지/리본 느낌을 준다
        clipPath: 'polygon(0 0, calc(100% - 26px) 0, 100% 26px, 100% 100%, 0 100%)',
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'box-shadow .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.10)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
    >
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <div style={{
          background: color, color: '#fff', height: 48, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 18px', flexShrink: 0, cursor: 'pointer',
        }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 17 }}>{icon}</span>{title}
          </span>
          <span title="바로가기" style={{
            width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
          }}>›</span>
        </div>
      </Link>
      <div style={{ background: '#FBF8F3', padding: '8px 18px 12px' }}>
        {loading ? (
          <div style={{ height: 32 * ROW_COUNT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12.5 }}>
            불러오는 중...
          </div>
        ) : (!items || !items.length) ? (
          <div style={{ height: 32 * ROW_COUNT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c8c4ba', fontSize: 12.5 }}>
            {empty}
          </div>
        ) : (
          rows.map((it, i) => (
            <div key={i} onClick={() => it && onItemClick && onItemClick(it)} style={{
              height: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              borderBottom: i < ROW_COUNT - 1 ? '1px solid #f0ece1' : 'none', cursor: it ? 'pointer' : 'default',
            }}>
              {it ? (
                <>
                  <span style={{
                    fontSize: 13, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
                  }}>
                    {it.dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: it.dot, flexShrink: 0 }} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                  </span>
                  {it.right && <span style={{ fontSize: 11.5, color: '#999', flexShrink: 0 }}>{it.right}</span>}
                </>
              ) : <span style={{ fontSize: 12, color: '#e5e0d5' }}>—</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── 하단 카드 항목 클릭시 뜨는 상세 모달 — 카드 종류(cardType)별로 원본 데이터(raw)에서
//    의미있는 필드만 뽑아 key-value 형태로 보여주고, 하단에 해당 메뉴로 이동하는 버튼을 둔다.
const CARD_ITEM_FIELDS = {
  notices: (raw) => [
    ['카테고리', raw.category], ['등록자', raw.created_by],
    ['등록일', raw.created_at], ['내용', raw.content],
  ],
  schedules: (raw) => [
    ['카테고리', raw.category], ['기간', `${raw.start_date || ''}${raw.end_date && raw.end_date !== raw.start_date ? ' ~ ' + raw.end_date : ''}`],
    ['장소', raw.location], ['등록자', raw.created_by], ['내용', raw.content],
  ],
  approvals: (raw) => [
    ['정보', raw.meta],
  ],
  tasks: (raw) => [
    ['구분', raw.category], ['담당자', raw.manager], ['우선순위', raw.priority],
    ['상태', raw.status], ['마감일', raw.due_date],
  ],
  sales: (raw) => [
    ['고객사', raw.customer], ['모델명', raw.model_no], ['옵션', raw.color],
    ['출고일', raw.ship_date], ['상태', raw.shipped_status],
  ],
  productions: (raw) => [
    ['모델명', raw.model_no], ['옵션', raw.color],
    ['생산수량', `${formatNum(raw.produced_qty)} / ${formatNum(raw.target_qty)}`], ['상태', raw.status],
  ],
};
function CardItemDetailModal({ cardType, item, onClose, onNavigate }) {
  const raw = item.raw || {};
  const fields = (CARD_ITEM_FIELDS[cardType]?.(raw) || []).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 420, maxWidth: '92vw', maxHeight: '80vh',
        overflowY: 'auto', padding: '22px 24px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 10 }}>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: '#1a1a1a' }}>{item.label}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#999', cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
          {fields.length === 0 ? (
            <div style={{ fontSize: 13, color: '#bbb' }}>표시할 상세 정보가 없습니다.</div>
          ) : fields.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 10, fontSize: 13 }}>
              <span style={{ color: '#999', minWidth: 64, flexShrink: 0 }}>{k}</span>
              <span style={{ color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={onNavigate} style={{
          width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#185FA5',
          color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
          해당 메뉴에서 보기 →
        </button>
      </div>
    </div>
  );
}

// ── 환율 위젯 (무료 공개 API, 서버가 30분 캐시) ──
// 전일대비(직전 거래일 대비) 등락에 따라 숫자 자체를 빨간색(상승)/파란색(하락)으로 칠하고,
// 그 오른쪽에 증감량(+3 / -5 같은 식)을 함께 보여준다.
const fxTrendColor = (t) => t === 'up' ? '#dc2626' : t === 'down' ? '#185FA5' : '#222';
const fxTrendArrow = (t) => t === 'up' ? '▲' : t === 'down' ? '▼' : '';
function FxValueRow({ flag, label, value, trend, diff, decimals }) {
  const color = fxTrendColor(trend);
  const showDiff = trend !== 'flat' && diff;
  const diffText = showDiff ? `${diff > 0 ? '+' : ''}${decimals ? diff.toFixed(decimals) : diff}` : null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontSize: 12.5, color: '#666' }}>{flag} {label}</span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color }}>{value != null ? formatNum(value) : '-'}</span>
        {diffText && <span style={{ fontSize: 11, fontWeight: 600, color }}>{fxTrendArrow(trend)} {diffText}</span>}
      </span>
    </div>
  );
}
function ExchangeRateWidget({ fx, loading, refreshing, onRefresh }) {
  return (
    <div style={{ minWidth: 190 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 10 }}>
        <span onClick={onRefresh} title={onRefresh ? '클릭하여 즉시 갱신' : undefined}
          style={{ fontSize: 13, fontWeight: 700, color: '#333', cursor: onRefresh ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}>
          💱 환율
          {onRefresh && <span style={{ fontSize: 11, color: '#aaa', animation: refreshing ? 'fxSpin 0.8s linear infinite' : 'none', display: 'inline-block' }}>⟳</span>}
        </span>
        <span style={{ fontSize: 10.5, color: '#aaa', whiteSpace: 'nowrap' }}>
          {refreshing ? '갱신중...' : (!loading && fx?.updatedAt ? `${minutesAgo(fx.updatedAt)} 갱신` : '')}
        </span>
      </div>
      {onRefresh && <style>{`@keyframes fxSpin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>}
      {loading ? (
        <div style={{ fontSize: 12, color: '#bbb' }}>불러오는 중...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <FxValueRow flag="🇺🇸" label="USD" value={fx?.usd} trend={fx?.usdTrend} diff={fx?.usdDiff} decimals={0} />
          <FxValueRow flag="🇨🇳" label="CNY" value={fx?.cny} trend={fx?.cnyTrend} diff={fx?.cnyDiff} decimals={2} />
        </div>
      )}
    </div>
  );
}

// ── 날씨 위젯 (접속 위치 기준, 무료 공개 API, 서버가 1분 캐시) ──
// 가로폭을 기존 대비 2배로 넓혀서 아이콘/기온을 크게 보여주고, 나머지 정보(최고/최저·미세먼지·
// 초미세먼지)는 오른쪽에 각각 라벨+값이 있는 통계 항목 형태로 넓게 펼쳐서 배치한다.
function StatItem({ label, value, size = 18, weight = 600 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 64 }}>
      <span style={{ fontSize: 13, color: '#999' }}>{label}</span>
      <span style={{ fontSize: size, fontWeight: weight, color: '#333' }}>{value}</span>
    </div>
  );
}
// 대한민국 주요 도시 — 날씨 카드를 클릭하면 뜨는 지역 선택 모달에서 고를 수 있는 목록(좌표 포함).
const KOREA_CITIES = [
  { name: '서울', lat: 37.5665, lon: 126.9780 }, { name: '부산', lat: 35.1796, lon: 129.0756 },
  { name: '대구', lat: 35.8714, lon: 128.6014 }, { name: '인천', lat: 37.4563, lon: 126.7052 },
  { name: '광주', lat: 35.1595, lon: 126.8526 }, { name: '대전', lat: 36.3504, lon: 127.3845 },
  { name: '울산', lat: 35.5384, lon: 129.3114 }, { name: '세종', lat: 36.4801, lon: 127.2890 },
  { name: '수원', lat: 37.2636, lon: 127.0286 }, { name: '창원', lat: 35.2281, lon: 128.6811 },
  { name: '청주', lat: 36.6424, lon: 127.4890 }, { name: '전주', lat: 35.8242, lon: 127.1480 },
  { name: '포항', lat: 36.0190, lon: 129.3435 }, { name: '제주', lat: 33.4996, lon: 126.5312 },
  { name: '강릉', lat: 37.7519, lon: 128.8761 }, { name: '춘천', lat: 37.8813, lon: 127.7298 },
];
// 대한민국 시/도 → 시/군/구 계층 — "상세 지역 선택"에서 캐스케이딩(단계적) 드롭다운으로 고른다.
// 세종특별자치시는 하위 시/군/구가 없는 단일 행정구역이라 자기 자신 하나만 넣어둔다.
// 좌표는 여기 넣지 않는다 — 선택한 시/도+시/군/구(+동/읍/면 직접입력)를 문자열로 합쳐서
// 백엔드가 Open-Meteo 지오코딩 API로 실시간 변환한다(수천 개의 동/읍 좌표를 미리 담아둘 필요가 없음).
const KOREA_SIDO_SIGUNGU = {
  '서울특별시': ['종로구','중구','용산구','성동구','광진구','동대문구','중랑구','성북구','강북구','도봉구','노원구','은평구','서대문구','마포구','양천구','강서구','구로구','금천구','영등포구','동작구','관악구','서초구','강남구','송파구','강동구'],
  '부산광역시': ['중구','서구','동구','영도구','부산진구','동래구','남구','북구','해운대구','사하구','금정구','강서구','연제구','수영구','사상구','기장군'],
  '대구광역시': ['중구','동구','서구','남구','북구','수성구','달서구','달성군','군위군'],
  '인천광역시': ['중구','동구','미추홀구','연수구','남동구','부평구','계양구','서구','강화군','옹진군'],
  '광주광역시': ['동구','서구','남구','북구','광산구'],
  '대전광역시': ['동구','중구','서구','유성구','대덕구'],
  '울산광역시': ['중구','남구','동구','북구','울주군'],
  '세종특별자치시': ['세종특별자치시'],
  '경기도': ['수원시','성남시','의정부시','안양시','부천시','광명시','평택시','동두천시','안산시','고양시','과천시','구리시','남양주시','오산시','시흥시','군포시','의왕시','하남시','용인시','파주시','이천시','안성시','김포시','화성시','광주시','양주시','포천시','여주시','연천군','가평군','양평군'],
  '강원특별자치도': ['춘천시','원주시','강릉시','동해시','태백시','속초시','삼척시','홍천군','횡성군','영월군','평창군','정선군','철원군','화천군','양구군','인제군','고성군','양양군'],
  '충청북도': ['청주시','충주시','제천시','보은군','옥천군','영동군','증평군','진천군','괴산군','음성군','단양군'],
  '충청남도': ['천안시','공주시','보령시','아산시','서산시','논산시','계룡시','당진시','금산군','부여군','서천군','청양군','홍성군','예산군','태안군'],
  '전북특별자치도': ['전주시','군산시','익산시','정읍시','남원시','김제시','완주군','진안군','무주군','장수군','임실군','순창군','고창군','부안군'],
  '전라남도': ['목포시','여수시','순천시','나주시','광양시','담양군','곡성군','구례군','고흥군','보성군','화순군','장흥군','강진군','해남군','영암군','무안군','함평군','영광군','장성군','완도군','진도군','신안군'],
  '경상북도': ['포항시','경주시','김천시','안동시','구미시','영주시','영천시','상주시','문경시','경산시','의성군','청송군','영양군','영덕군','청도군','고령군','성주군','칠곡군','예천군','봉화군','울진군','울릉군'],
  '경상남도': ['창원시','진주시','통영시','사천시','김해시','밀양시','거제시','양산시','의령군','함안군','창녕군','고성군','남해군','하동군','산청군','함양군','거창군','합천군'],
  '제주특별자치도': ['제주시','서귀포시'],
};
const KOREA_SIDO_LIST = Object.keys(KOREA_SIDO_SIGUNGU);

// 날씨 카드를 클릭하면 뜨는 지역 선택 모달 — 고른 지역을 부모(Home)의 cityOverride로 저장하고
// 즉시 그 지역 날씨를 다시 조회한다. 위쪽엔 자주 쓰는 주요 도시 버튼(빠른 선택), 아래쪽엔
// 시/도 → 시/군/구 → 동/읍/면(직접입력) 단계별 선택으로 더 정확한 좌표를 찾을 수 있다.
function CityPickerModal({ current, onSelect, onClose }) {
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [dong, setDong] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState('');

  const applyDetailed = async () => {
    if (!sido || !sigungu) { setSearchErr('시/도와 시/군/구를 선택해주세요.'); return; }
    setSearching(true); setSearchErr('');
    const q = [sido, sigungu, dong.trim()].filter(Boolean).join(' ');
    try {
      const r = await API.get('/dashboard/geocode', { params: { q } });
      const label = dong.trim() ? `${sigungu} ${dong.trim()}` : sigungu;
      onSelect({ name: label, lat: r.data.lat, lon: r.data.lon });
    } catch (err) {
      setSearchErr(err.response?.data?.message || '해당 지역의 좌표를 찾지 못했습니다. 다른 지역명으로 시도해주세요.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '90vw', maxHeight: '85vh',
        overflowY: 'auto', padding: '22px 24px' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>🌦️ 날씨 지역 선택</div>

        <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>자주 찾는 지역</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {KOREA_CITIES.map(c => (
            <button key={c.name} onClick={() => onSelect(c)}
              style={{ padding: '10px 6px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: current?.name === c.name ? '2px solid #185FA5' : '1px solid #e5e7eb',
                background: current?.name === c.name ? '#e8f1fb' : '#fff',
                color: current?.name === c.name ? '#185FA5' : '#333' }}>
              {c.name}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
            상세 지역 선택 (시/군/구/동/읍) — 정확한 날씨를 위해 세부 지역까지 선택할 수 있습니다.
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <select value={sido} onChange={e => { setSido(e.target.value); setSigungu(''); }}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, outline: 'none' }}>
              <option value="">시/도 선택</option>
              {KOREA_SIDO_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={sigungu} onChange={e => setSigungu(e.target.value)} disabled={!sido}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, outline: 'none',
                background: sido ? '#fff' : '#f5f5f5' }}>
              <option value="">시/군/구 선택</option>
              {(KOREA_SIDO_SIGUNGU[sido] || []).map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={dong} onChange={e => setDong(e.target.value)} placeholder="읍/면/동 (선택, 예: 역삼동)"
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, outline: 'none' }} />
            <button onClick={applyDetailed} disabled={searching || !sido || !sigungu}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
                cursor: (searching || !sido || !sigungu) ? 'not-allowed' : 'pointer',
                background: (searching || !sido || !sigungu) ? '#ccc' : '#185FA5', color: '#fff', whiteSpace: 'nowrap' }}>
              {searching ? '검색 중...' : '적용'}
            </button>
          </div>
          {searchErr && <div style={{ marginTop: 6, fontSize: 12, color: '#dc2626' }}>{searchErr}</div>}
        </div>

        <div style={{ marginTop: 18, textAlign: 'right' }}>
          <button onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
function WeatherWidget({ wx, loading, cityLabel, onPick }) {
  return (
    <div style={{ minWidth: 540 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 10 }}>
        <span onClick={onPick} title={onPick ? '클릭하여 지역 변경' : undefined}
          style={{ fontSize: 14, fontWeight: 700, color: '#333', cursor: onPick ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: 4 }}>
          🌦️ 날씨 · {cityLabel || (!loading && wx?.city) || '울산'}
          {onPick && <span style={{ fontSize: 10, color: '#aaa' }}>▾</span>}
        </span>
        <span style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap' }}>{!loading && wx?.updatedAt ? `${minutesAgo(wx.updatedAt)} 갱신` : ''}</span>
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#bbb' }}>불러오는 중...</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <span style={{ fontSize: 42 }}>{wx?.icon || '🌡️'}</span>
            <div>
              <div style={{ fontSize: 30, fontWeight: 700, color: '#222', lineHeight: 1 }}>{wx?.temp != null ? `${wx.temp}°C` : '-'}</div>
              <div style={{ fontSize: 12.5, color: '#888', marginTop: 4 }}>{wx?.label || ''}</div>
            </div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: '#f0ece1', flexShrink: 0 }} />
          <div style={{ display: 'flex', gap: 22, flex: 1 }}>
            <StatItem label="최고" value={wx?.high != null ? `${wx.high}°C` : '-'} size={19} weight={700} />
            <StatItem label="최저" value={wx?.low != null ? `${wx.low}°C` : '-'} size={19} weight={700} />
            {/* 2026-09-02 — "최저와 미세먼지 사이에 습도 추가" 요청. 기존 날씨 플랫폼(Open-Meteo)
                응답값(wx.humidity)을 그대로 표시 */}
            <StatItem label="습도" value={wx?.humidity != null ? `${wx.humidity}%` : '-'} size={19} weight={700} />
            <StatItem label="미세먼지" value={wx?.pm10Grade || '-'} size={17} />
            <StatItem label="초미세먼지" value={wx?.pm25Grade || '-'} size={17} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── 소셜 채널 바로가기 버튼 (인사말 카드 하단) ──
// 예전엔 "오늘 게시물 수(네이버/구글)" 위젯이 따로 있었는데, 실제로 유용한 건 바로가기라
// 클릭하면 각 채널로 바로 이동하는 원형 버튼으로 대체했다.
// 각 채널의 대표색을 그대로 원형 배경에 써서 한눈에 어떤 채널인지 구분되게 한다. 새 탭으로 연다.
const SOCIAL_LINKS = [
  { key: 'naver', label: '네이버', url: 'https://blog.naver.com/ninesafety', bg: '#03C75A',
    icon: <span style={{ fontSize: 17, fontWeight: 800, color: '#fff' }}>N</span> },
  { key: 'google', label: '구글', url: 'https://ninesafety.blogspot.com/', bg: '#4285F4',
    icon: <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>G</span> },
  { key: 'youtube', label: '유튜브', url: 'https://www.youtube.com/@NineSafety', bg: '#FF0000',
    icon: (
      <svg width="25" height="25" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
    ) },
  { key: 'tiktok', label: '틱톡', url: 'https://www.tiktok.com/@nine_safety', bg: '#010101',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
        <path d="M16.6 5.82c-.9-.98-1.4-2.26-1.4-3.63h-3.28v13.44c0 1.53-1.24 2.77-2.77 2.77a2.77 2.77 0 0 1 0-5.54c.3 0 .58.05.85.13V9.62a6.06 6.06 0 0 0-.85-.06 6.06 6.06 0 1 0 6.06 6.06V9.15a8.24 8.24 0 0 0 4.85 1.56V7.44a4.85 4.85 0 0 1-3.46-1.62z" />
      </svg>
    ) },
  { key: 'instagram', label: '인스타그램', url: 'https://www.instagram.com/NineSafety/',
    bg: 'linear-gradient(135deg,#f9ce34,#ee2a7b 55%,#6228d7)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="#fff" stroke="none" />
      </svg>
    ) },
  { key: 'facebook', label: '페이스북', url: 'https://www.facebook.com/people/NineSafety/61563657466945/', bg: '#1877F2',
    icon: <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', fontFamily: 'Georgia, serif' }}>f</span> },
];
// 계열사/자사몰 바로가기 — 소셜 채널과는 구분되는 회사 사이트 3곳.
const COMPANY_LINKS = [
  { key: 'ninegro', label: '주식회사 나인', url: 'https://www.ninegro.com', bg: '#1B2951',
    icon: <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>N</span> },
  { key: 'ninecase', label: '나인케이스 블로그', url: 'https://blog.naver.com/ninegro/', bg: '#1DB954',
    icon: <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>b</span> },
  { key: 'ninesafety', label: '나인세이프티', url: 'https://www.ninesafety.com', bg: '#111827',
    icon: <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>N</span> },
];
function SocialIconLink({ s }) {
  return (
    <a href={s.url} target="_blank" rel="noopener noreferrer" title={s.label}
      style={{
        width: 40, height: 40, borderRadius: '50%', background: s.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        transition: 'transform .15s', textDecoration: 'none',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
      {s.icon}
    </a>
  );
}
function SocialButtons() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        {SOCIAL_LINKS.map(s => <SocialIconLink key={s.key} s={s} />)}
      </div>
      <div style={{ width: 1, height: 28, background: '#f0ece1' }} />
      <div style={{ display: 'flex', gap: 10 }}>
        {COMPANY_LINKS.map(s => <SocialIconLink key={s.key} s={s} />)}
      </div>
    </div>
  );
}

// ── 근태체크 위젯(홈 화면에서 바로 출근/퇴근/외근/출장/복귀 기록) ──
// 인사관리 > 근태관리 목록에 실제로 반영되는 진짜 체크인 버튼이다. 이미 오늘자 출근/퇴근이
// 기록되어 있으면 해당 버튼은 완료 표시(✓)로 바뀌고 다시 누를 수 없다. 외근/출장은 누르면 오늘자
// 상태가 해당 값으로 바뀌고, 복귀를 누르면 다시 '정상' 상태로 되돌아온다(외근·출장 중일 때만 활성화).
function AttendanceCheckWidget({ today, loading, busy, onCheck }) {
  const inDone = !!today?.check_in;
  const outDone = !!today?.check_out;
  const isAway = today?.status === '외근' || today?.status === '출장';
  return (
    <div style={{ marginTop: 14 }}>
      {loading ? (
        <div style={{ fontSize: 12, color: '#bbb' }}>불러오는 중...</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, maxWidth: 460 }}>
          {/* 버튼 아래 시간 표기 — 각 버튼 바로 아래, 그 버튼의 폭(flex:1) 기준으로 가운데 정렬.
              퇴근이 기록된 이후에는 외근/출장/복귀를 다시 누를 수 없다(하루 근무 종료로 간주). */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <button disabled={busy || inDone} onClick={() => onCheck('in')}
              style={{ width: '100%', padding: '3px 0', borderRadius: 8, border: 'none', fontSize: 12.5, fontWeight: 700,
                cursor: (busy || inDone) ? 'not-allowed' : 'pointer',
                background: inDone ? '#dcfce7' : '#185FA5', color: inDone ? '#16a34a' : '#fff' }}>
              {inDone ? '✓ 출근' : '출근'}
            </button>
            <span style={{ fontSize: 13, color: '#666666' }}>{inDone ? today.check_in : ' '}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <button disabled={busy || !inDone || outDone} onClick={() => onCheck('out')}
              style={{ width: '100%', padding: '3px 0', borderRadius: 8, border: 'none', fontSize: 12.5, fontWeight: 700,
                cursor: (busy || !inDone || outDone) ? 'not-allowed' : 'pointer',
                background: outDone ? '#fee2e2' : (!inDone ? '#e5e7eb' : '#C1553A'),
                color: outDone ? '#dc2626' : (!inDone ? '#aaa' : '#fff') }}>
              {outDone ? '✓ 퇴근' : '퇴근'}
            </button>
            <span style={{ fontSize: 13, color: '#666666' }}>{outDone ? today.check_out : ' '}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <button disabled={busy || outDone} onClick={() => onCheck('field')}
              style={{ width: '100%', padding: '3px 0', borderRadius: 8, border: '1px solid #C99A4B', fontSize: 12.5, fontWeight: 700,
                cursor: (busy || outDone) ? 'not-allowed' : 'pointer',
                background: today?.status === '외근' ? '#fdf3e3' : '#fff', color: outDone ? '#ccc' : '#C99A4B' }}>
              외근
            </button>
            <span style={{ fontSize: 13, color: '#666666' }}>{today?.field_time || (today?.status === '외근' ? '진행중' : ' ')}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <button disabled={busy || outDone} onClick={() => onCheck('trip')}
              style={{ width: '100%', padding: '3px 0', borderRadius: 8, border: '1px solid #4F7A63', fontSize: 12.5, fontWeight: 700,
                cursor: (busy || outDone) ? 'not-allowed' : 'pointer',
                background: today?.status === '출장' ? '#eaf3ee' : '#fff', color: outDone ? '#ccc' : '#4F7A63' }}>
              출장
            </button>
            <span style={{ fontSize: 13, color: '#666666' }}>{today?.trip_time || (today?.status === '출장' ? '진행중' : ' ')}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <button disabled={busy || outDone || !isAway} onClick={() => onCheck('return')}
              style={{ width: '100%', padding: '3px 0', borderRadius: 8, border: '1px solid #999', fontSize: 12.5, fontWeight: 700,
                cursor: (busy || outDone || !isAway) ? 'not-allowed' : 'pointer',
                background: '#fff', color: (isAway && !outDone) ? '#555' : '#ccc' }}>
              복귀
            </button>
            <span style={{ fontSize: 13, color: '#666666' }}>{today?.return_time || ' '}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 오늘 콘텐츠 현황 (채널별 업로드 수) ──
// 네이버/구글(블로그스팟)/유튜브는 공개 RSS·JSON 피드로 인증 없이 오늘 올라온 글/영상 수를 셀 수
// 있어 실제 값을 보여준다(자동집계). 인스타그램/페이스북/틱톡은 각 플랫폼이 발급하는 정식 API
// 키(Meta 개발자 앱 심사, 틱톡 API 등)가 있어야만 조회가 가능해서, 숫자를 클릭하면 사용자가 오늘
// 등록한 수량을 직접 입력하도록 했다(직접입력이 없으면 0건으로 표시).
// 채널 아이콘/색상은 아래 소셜 바로가기 버튼(SOCIAL_LINKS)과 완전히 동일한 걸 그대로 재사용해서
// 어떤 채널인지 한눈에 매칭되게 한다.
const CONTENT_CHANNEL_KEYS = ['naver', 'google', 'youtube', 'tiktok', 'instagram', 'facebook'];
const MANUAL_SOCIAL_CHANNELS = ['tiktok', 'instagram', 'facebook'];

function ContentUploadWidget({ social, loading, onOpenStats, onSaveManual }) {
  const linkByKey = Object.fromEntries(SOCIAL_LINKS.map(s => [s.key, s]));
  // 틱톡/인스타/페이스북 숫자를 클릭하면 그 채널만 인라인 입력창으로 바뀐다.
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (key, current) => { setEditingKey(key); setEditValue(String(current ?? 0)); };
  const commitEdit = () => {
    const key = editingKey;
    if (!key) return;
    setEditingKey(null);
    onSaveManual(key, editValue);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 10 }}>
        {/* 글자를 누르면 채널별 오늘/전체/월별/일자별 통계 + 다운로드가 가능한 상세 모달이 뜬다 */}
        <span onClick={() => onOpenStats(CONTENT_CHANNEL_KEYS[0])} title="클릭하여 상세 통계 보기"
          style={{ fontSize: 13, fontWeight: 700, color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          📊 소셜 업데이트<span style={{ fontSize: 10, color: '#aaa' }}>▾</span>
        </span>
        <span style={{ fontSize: 10.5, color: '#aaa', whiteSpace: 'nowrap' }}>{!loading && social?.updatedAt ? `${minutesAgo(social.updatedAt)} 갱신` : ''}</span>
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#bbb' }}>불러오는 중...</div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
          {CONTENT_CHANNEL_KEYS.map(key => {
            const s = linkByKey[key];
            const info = social?.[key];
            const isManual = MANUAL_SOCIAL_CHANNELS.includes(key);
            return (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1 }}>
                {/* 아이콘 클릭 시 해당 플랫폼(SOCIAL_LINKS의 url)으로 새 탭 이동 */}
                <a href={s.url} target="_blank" rel="noopener noreferrer" title={s.label} style={{
                  width: 34, height: 34, borderRadius: '50%', background: s.bg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0, textDecoration: 'none',
                  cursor: 'pointer',
                }}>{s.icon}</a>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {editingKey === key ? (
                    <input autoFocus type="number" min={0} value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingKey(null); }}
                      style={{ width: 46, fontSize: 12.5, padding: '2px 4px', border: '1px solid #185FA5', borderRadius: 4, textAlign: 'center' }} />
                  ) : isManual ? (
                    <span onClick={() => startEdit(key, info?.count)} title="클릭하여 직접입력"
                      style={{ fontSize: 14, fontWeight: 700, color: '#333', cursor: 'pointer', borderBottom: '1px dashed #ccc' }}>
                      {info?.count ?? 0}건
                    </span>
                  ) : info?.error ? (
                    <span onClick={() => onOpenStats(key)} title="클릭하여 상세보기"
                      style={{ fontSize: 11, color: '#c8c4ba', cursor: 'pointer' }}>확인불가</span>
                  ) : (
                    <span onClick={() => onOpenStats(key)} title="클릭하여 상세보기"
                      style={{ fontSize: 14, fontWeight: 700, color: '#333', cursor: 'pointer' }}>
                      {info?.count != null ? `${info.count}건` : '-'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 소셜 업데이트 상세 모달 — "소셜 업데이트" 글자 또는 채널별 숫자를 클릭하면 뜬다 ──
// 채널별로 탭을 나눠 보던 예전 방식 대신, 6개 채널을 한 표에 나란히 놓고 한눈에 비교할 수 있는
// 통합표 방식으로 개편. 상단 요약(오늘/이번달/전체)도 채널 구분 없이 전체 합산 수치다.
const SOCIAL_CHANNEL_LABELS = Object.fromEntries(SOCIAL_LINKS.map(s => [s.key, s.label]));
const SOCIAL_TABLE_COLUMNS = [
  { key: 'naver', label: '네이버' }, { key: 'google', label: '구글' }, { key: 'youtube', label: '유튜브' },
  { key: 'tiktok', label: '틱톡' }, { key: 'instagram', label: '인스타그램' }, { key: 'facebook', label: '페이스북' },
];
const SOCIAL_VIEW_OPTIONS = [ { key: 'daily', label: '일별' }, { key: 'monthly', label: '월별' }, { key: 'all', label: '전체' } ];
function SocialStatsModal({ onClose }) {
  const [view, setView] = useState('daily');
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get('/dashboard/social-posts/stats-all', { params: { view, month } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [view, month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, (m - 1) + delta, 1);
    setMonth(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  };

  const download = async () => {
    try {
      const res = await API.get('/dashboard/social-posts/stats-all/export', { params: { view, month }, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = `IMS_소셜업데이트_${view}_${month}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch { alert('다운로드 중 오류가 발생했습니다.'); }
  };

  const navBtn = { width: 26, height: 26, border: '1px solid #ddd', borderRadius: 6, background: '#fff', color: '#555', fontSize: 12, cursor: 'pointer' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 760, maxWidth: '94vw', maxHeight: '86vh',
        overflowY: 'auto', padding: '22px 24px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>📊 소셜 업데이트 상세</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#999', cursor: 'pointer' }}>✕</button>
        </div>

        {loading || !data ? (
          <div style={{ padding: '30px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>불러오는 중...</div>
        ) : (
          <>
            {/* 상단 요약 3종 — 채널 구분 없이 전체 합산, 가로폭을 꽉 채워 균등 배치 */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 11.5, color: '#999', marginBottom: 4 }}>오늘 등록</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#185FA5' }}>{formatNum(data.today)}건</div>
              </div>
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 11.5, color: '#999', marginBottom: 4 }}>이번달 등록</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0F6E56' }}>{formatNum(data.thisMonth)}건</div>
              </div>
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 11.5, color: '#999', marginBottom: 4 }}>전체 누적</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#333' }}>{formatNum(data.total)}건</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {SOCIAL_VIEW_OPTIONS.map(o => (
                  <button key={o.key} onClick={() => setView(o.key)}
                    style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      border: view === o.key ? '1.5px solid #185FA5' : '1px solid #e5e7eb',
                      background: view === o.key ? '#E6F1FB' : '#fff',
                      color: view === o.key ? '#185FA5' : '#666' }}>
                    {o.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {view === 'daily' && (
                  <>
                    <button onClick={() => shiftMonth(-1)} style={navBtn}>◀</button>
                    <span style={{ fontSize: 13, fontWeight: 600, minWidth: 66, textAlign: 'center' }}>{month}</span>
                    <button onClick={() => shiftMonth(1)} style={navBtn}>▶</button>
                  </>
                )}
                <button onClick={download}
                  style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#16a34a',
                    color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>⬇ 다운로드</button>
              </div>
            </div>

            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead><tr>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff' }}>
                    {view === 'monthly' ? '월' : view === 'all' ? '구분' : '일자'}
                  </th>
                  {SOCIAL_TABLE_COLUMNS.map(c => (
                    <th key={c.key} style={{ padding: '6px 10px', textAlign: 'right', color: '#888', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff', whiteSpace: 'nowrap' }}>{c.label}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(!data.rows || data.rows.length === 0) ? (
                    <tr><td colSpan={SOCIAL_TABLE_COLUMNS.length + 1} style={{ padding: '20px 0', textAlign: 'center', color: '#bbb' }}>기록이 없습니다.</td></tr>
                  ) : data.rows.map(r => (
                    <tr key={r.key}>
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #f5f5f5', fontWeight: 600 }}>{r.key}</td>
                      {SOCIAL_TABLE_COLUMNS.map(c => (
                        <td key={c.key} style={{ padding: '6px 10px', borderBottom: '1px solid #f5f5f5', textAlign: 'right' }}>{formatNum(r[c.key])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── 오늘 진행현황 요약 타일(신규, 한 줄) — 주요일정/업무관리/결재요청/영업관리/출고관리/생산관리를
// 로그인 당일 기준으로 "지금 진행 중인" 건수만 압축해서 한 줄에 보여준다. 클릭하면 해당 목록의
// 상세보기 모달이 뜨고, 모달 안 "전체보기" 버튼을 누르면 그 메뉴 페이지로 바로 이동한다.
// 기존에 만들어져 있던 하단 6개 카드(공지사항 포함) 영역은 그대로 유지하고 그 위에 얹는 형태.
// 원형(도넛) 그래프 형태 — 사각 아이콘 박스 대신 링(도넛) 안에 아이콘을 놓고, 건수에 비례해
// 링이 채워지는 정도를 장식적으로 다르게 해서(건수가 많을수록 더 채워짐) 한눈에 "양"이 느껴지게
// 한다. 카드 자체 padding/gap도 넓혀 답답해 보이지 않도록 여유를 뒀다.
function SummaryTile({ icon, title, color, count, loading, onClick }) {
  const R = 23, C = 2 * Math.PI * R;
  // 장식용 채움비율 — 정확한 통계적 의미보다는 "건수가 많을수록 원이 더 찬다"는 시각적 힌트가 목적.
  const pct = loading ? 0 : Math.max(count > 0 ? 10 : 0, Math.min(100, (count || 0) * 9));
  const dash = (pct / 100) * C;
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, minWidth: 0, background: '#fff', borderRadius: 14, padding: '18px 20px',
        display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow .15s, transform .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.10)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <svg width={58} height={58} viewBox="0 0 58 58" style={{ flexShrink: 0 }}>
        <circle cx={29} cy={29} r={R} fill="none" stroke="#f0ece1" strokeWidth={6} />
        {!loading && (
          <circle cx={29} cy={29} r={R} fill="none" stroke={color} strokeWidth={6}
            strokeDasharray={`${dash} ${C - dash}`} strokeLinecap="round"
            transform="rotate(-90 29 29)" style={{ transition: 'stroke-dasharray .4s' }} />
        )}
        <text x={29} y={30} textAnchor="middle" dominantBaseline="central" fontSize={18}>{icon}</text>
      </svg>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#222' }}>{loading ? <span style={{ color: '#ccc' }}>-</span> : `${formatNum(count)}건`}</div>
      </div>
    </div>
  );
}
// 요약 타일 클릭 시 뜨는 상세보기 모달 — 해당 항목 전체 목록 + "전체보기"(메뉴 이동) 버튼.
function TileDetailModal({ tile, onNavigate, onClose }) {
  const items = tile.items || [];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 480, maxWidth: '92vw', maxHeight: '80vh',
        overflowY: 'auto', padding: '22px 24px', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: tile.color, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>{tile.icon}</span>
            {tile.title}
            <span style={{ fontSize: 12, color: '#999', fontWeight: 500 }}>({items.length}건)</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#999', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ border: '1px solid #f0ece1', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
          {items.length === 0 ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>표시할 항목이 없습니다.</div>
          ) : items.map((it, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderBottom: i < items.length - 1 ? '1px solid #f5f5f5' : 'none',
            }}>
              <span style={{ fontSize: 13, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {it.dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: it.dot, flexShrink: 0 }} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
              </span>
              {it.right && <span style={{ fontSize: 11.5, color: '#999', flexShrink: 0 }}>{it.right}</span>}
            </div>
          ))}
        </div>
        <button onClick={onNavigate} style={{
          width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: tile.color,
          color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
          전체보기 →
        </button>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  // hasDetail('home','checkin') — 권한설정 > 상세설정 > 홈화면 > 읽기 > "출퇴근" 세부권한.
  // 이 권한이 켜져 있어야 아래 근태체크 위젯(버튼)이 보이고 동작한다(2026-08 추가). 인사관리
  // 메뉴(hr-management) 자체의 읽기/쓰기 권한과는 완전히 독립적으로 이 권한 하나로 판단한다
  // — 처음 구현 때는 canRead('hr-management')도 함께 요구하도록 되어 있어서, "홈화면 > 읽기 >
  // 출퇴근"만 켜준 사용자는 위젯이 안 뜨는 버그가 있었다. 백엔드(hr.js의 attendance/today,
  // attendance/check)도 동일하게 hr-management 게이트 없이 이 세부권한만으로 독립 동작한다.
  const { loading: permLoading, canRead, hasDetail } = usePermissions();
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loaded, setLoaded] = useState({});
  // 회원가입 후 첫 로그인이면 Login.js가 남겨둔 표시를 읽어 안내 배너를 띄운다. sessionStorage에서
  // 바로 지워서(1회성) 다른 페이지를 갔다와도 다시 뜨지 않게 하되, 배너 자체는 사용자가 닫거나
  // "추가정보 입력" 버튼을 눌러 마이페이지로 이동하기 전까지 이 화면에 계속 보여준다.
  const [showFirstLoginBanner, setShowFirstLoginBanner] = useState(() => {
    const pending = sessionStorage.getItem('ims_first_login_pending') === '1';
    if (pending) sessionStorage.removeItem('ims_first_login_pending');
    return pending;
  });
  // 날씨 카드를 클릭해서 직접 고른 지역(있으면 브라우저 GPS/IP 추정 대신 이 좌표를 우선 사용).
  // 새로고침해도 유지되도록 localStorage에 저장해둔다.
  const [cityOverride, setCityOverride] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ims_home_city') || 'null'); } catch { return null; }
  });
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [attBusy, setAttBusy] = useState(false);
  // 인사관리 > 사원정보 > 옵션(탭 메뉴 사용여부)에서 "홈 화면 근태체크 위젯"을 끌 수 있다
  // (tab_key: home_attendance_widget, HRManagement.js의 EXTRA_OPTIONS 참고). 기본값은 사용(true).
  const [attWidgetEnabled, setAttWidgetEnabled] = useState(true);
  // "소셜 업데이트" 글자를 클릭하면 여는 상세 통계 모달 — null이면 닫힘, 채널 key면 그 채널 탭이 열림.
  const [showSocialStats, setShowSocialStats] = useState(false);
  // 상단 "오늘 진행현황" 요약 타일 클릭 시 열리는 상세보기 모달 — tile 설정 객체 자체를 담아둔다(null이면 닫힘).
  const [openTile, setOpenTile] = useState(null);
  // 하단 카드(공지사항/일정/결재요청 등) 리스트에서 항목 한 줄을 클릭했을 때 열리는 상세 모달 —
  // { cardType, item, to } 형태로 담아둔다(null이면 닫힘).
  const [openCardItem, setOpenCardItem] = useState(null);
  // 1분 주기 setInterval 클로저 안에서도 항상 최신 cityOverride 값을 읽을 수 있도록 ref로 함께 관리.
  const cityOverrideRef = useRef(cityOverride);
  useEffect(() => { cityOverrideRef.current = cityOverride; }, [cityOverride]);

  const load = (key, fn) => {
    return fn()
      .then(v => { setData(d => ({ ...d, [key]: v })); setLoaded(l => ({ ...l, [key]: true })); })
      .catch(() => setLoaded(l => ({ ...l, [key]: true })));
  };

  const fetchWeather = (lat, lon) =>
    API.get('/dashboard/weather', { params: lat != null ? { lat, lon } : {} }).then(r => r.data);
  // 우선순위: ① 사용자가 날씨 카드를 클릭해서 직접 고른 지역 → ② 브라우저 GPS 좌표 →
  // ③ 좌표 없이 요청(서버가 회사 고정 위치로 대체 조회).
  const fetchWeatherAuto = () => load('weather', () => new Promise((resolve, reject) => {
    const override = cityOverrideRef.current;
    if (override) { fetchWeather(override.lat, override.lon).then(resolve, reject); return; }
    if (!navigator.geolocation) { fetchWeather().then(resolve, reject); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude).then(resolve, reject),
      () => fetchWeather().then(resolve, reject),
      { timeout: 3000, maximumAge: 10 * 60 * 1000 }
    );
  }));
  // 지역 선택 모달에서 도시를 고르면: 저장 + 즉시 그 지역 날씨로 재조회.
  const selectCity = (city) => {
    setCityOverride(city);
    cityOverrideRef.current = city;
    try { localStorage.setItem('ims_home_city', JSON.stringify(city)); } catch { /* ignore */ }
    setShowCityPicker(false);
    fetchWeatherAuto();
  };

  // 오늘 콘텐츠 업로드 수(네이버/구글/유튜브 등) — 환율/날씨와 동일하게 로그인만 되면 누구나 볼 수
  // 있는 참고 정보이며, 서버도 1분 캐시라 같이 1분마다 재조회한다. 컴포넌트 스코프에 둬서 틱톡/
  // 인스타그램/페이스북 수동입력 저장 직후에도 바로 다시 불러올 수 있게 했다.
  const fetchSocial = () => load('social', async () => (await API.get('/dashboard/social-posts')).data);
  const saveManualSocial = (channel, count) => {
    API.post('/dashboard/social-posts/manual', { channel, count })
      .then(() => fetchSocial())
      .catch(() => alert('저장 중 오류가 발생했습니다.'));
  };

  // 환율/날씨는 권한과 무관하게 누구나 볼 수 있는 참고 정보라 로그인만 되면 바로 불러온다.
  // 두 위젯 모두 화면을 켜둔 채로 값이 최신 상태를 유지하도록 1분마다 자동 재조회한다
  // (서버도 1분 캐시라 실제 외부 API 호출은 그보다 뜸하게 일어남).
  // "💱 환율" 글자를 클릭하면 force=1로 요청해서 서버의 1분 캐시를 건너뛰고 즉시 재조회한다.
  const [fxRefreshing, setFxRefreshing] = useState(false);
  const fetchFx = (force) => load('fx', async () => (await API.get('/dashboard/exchange-rate', { params: force ? { force: 1 } : {} })).data);
  const refreshFx = () => { setFxRefreshing(true); fetchFx(true).finally(() => setFxRefreshing(false)); };

  // 인사말 옆 상태 배지(회의중/출장중/외근중/휴가중) — 근태관리 메뉴 권한과 무관하게 로그인한
  // 누구나 조회 가능한 /user-status/me를 사용한다. 환율/날씨와 동일하게 1분마다 자동 갱신해서
  // 회의가 끝나거나 자정이 지나 근태가 초기화되면 새로고침 없이도 배지가 스스로 사라진다.
  const fetchMyStatus = () => load('myStatus', async () => (await API.get('/user-status/me')).data?.status || null);

  useEffect(() => {
    fetchFx();
    fetchWeatherAuto();
    fetchSocial();
    fetchMyStatus();
    const timer = setInterval(() => { fetchFx(); fetchWeatherAuto(); fetchSocial(); fetchMyStatus(); }, 60 * 1000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (permLoading) return;

    if (canRead('notices')) load('notices', async () => {
      const rows = (await API.get('/notices')).data || [];
      return rows.slice(0, ROW_COUNT).map(n => ({
        label: (n.is_pinned ? '📌 ' : '') + n.title,
        right: shortDate(n.created_at),
        dot: n.category === '긴급' ? '#dc2626' : n.category === '중요' ? '#d97706' : '#185FA5',
        raw: n,
      }));
    });

    // ※ 아래 목록들은 하단 카드(5줄 고정) + 상단 요약 타일(전체 건수/상세모달)에서 함께 쓰기 위해
    // 슬라이스하지 않은 전체 배열을 그대로 저장해두고, 하단 카드 쪽에서만 렌더링 시 5개로 자른다.
    if (canRead('schedules')) load('schedules', async () => {
      const rows = (await API.get('/schedules')).data || [];
      const today = todayStr();
      const todays = rows.filter(s => s.start_date <= today && (s.end_date || s.start_date) >= today);
      return todays.map(s => ({ label: s.title, right: s.location || '', raw: s }));
    });

    if (canRead('tasks')) load('tasks', async () => {
      const rows = (await API.get('/tasks')).data || [];
      const inProgress = rows.filter(t => t.status === '진행중');
      const preview = inProgress.slice().sort((a, b) => (a.priority === '높음' ? -1 : 0) - (b.priority === '높음' ? -1 : 0));
      return preview.map(t => ({
        label: t.title, right: t.manager || '',
        dot: t.priority === '높음' ? '#dc2626' : undefined,
        raw: t,
      }));
    });

    // 근태체크 위젯 — "홈화면 > 읽기 > 출퇴근" 세부권한이 있으면 본인의 오늘자 근태 기록을
    // 불러온다. 인사관리 메뉴 자체의 읽기/쓰기 권한과는 무관하게 이 권한 하나로 독립 동작한다
    // (2026-08 수정 — 이전에는 canRead('hr-management')에 종속되어 있어서, hr-management
    // 메뉴 권한 없이 home/checkin 세부권한만 준 사용자에게는 위젯이 전혀 동작하지 않았다).
    if (hasDetail('home', 'checkin')) load('attendanceToday', async () => (await API.get('/hr/attendance/today')).data);

    if (canRead('sales')) load('sales', async () => {
      const rows = (await API.get('/shipments')).data || [];
      // 요약카드는 "오늘 기준 진행중이거나 미결된 사항"만 세므로, 이미 출고완료된 건은 제외한다.
      const deliveries = rows.filter(s => s.type === '납품' && s.shipped_status !== '완료')
        .sort((a, b) => (b.ship_date || '').localeCompare(a.ship_date || ''));
      return deliveries.map(s => ({
        label: `${s.customer || '-'} · ${s.model_no || '-'}`,
        right: shortDate(s.ship_date),
        dot: s.shipped_status === '완료' ? '#0F6E56' : s.shipped_status === '보류' ? '#dc2626' : '#999',
        raw: s,
      }));
    });

    if (canRead('productions')) load('productions', async () => {
      const rows = (await API.get('/productions/status').catch(() => ({ data: [] }))).data || [];
      const inProgress = rows.filter(s => s.status !== '생산완료');
      return inProgress.map(s => ({
        label: `${s.model_no || '-'}${s.color ? ` (${s.color})` : ''}`,
        right: `${formatNum(s.produced_qty)}/${formatNum(s.target_qty)}`,
        raw: s,
      }));
    });

    // 결재요청 — 생산관리(생산계획 승인대기) + 출고관리(결재확인 대기)를 한 곳에 모아 보여준다.
    // 둘 중 하나라도 메뉴 접근권한이 있으면 카드 자체는 보여주되, 실제 처리는 각 메뉴에서
    // 승인권한이 있는 사람만 할 수 있다(여기서는 목록만 보여줌).
    if (canRead('productions') || canRead('sales')) load('approvals', async () => {
      const r = (await API.get('/dashboard/approvals')).data || { production: [], shipment: [] };
      return [...r.production, ...r.shipment].map(a => ({ label: a.label, right: a.meta, raw: a }));
    });

    // 영업관리 — 진행중인 영업활동(방문/상담 등)만 오늘 기준 요약 타일에 표시.
    if (canRead('sales-activities')) load('salesActivities', async () => {
      const rows = (await API.get('/sales-activities').catch(() => ({ data: [] }))).data || [];
      const inProgress = rows.filter(a => (a.status || '진행중') === '진행중');
      return inProgress.map(a => ({ label: a.customer_company || '-', right: shortDate(a.activity_date) }));
    });
  }, [permLoading]);

  // 근태체크 위젯이 자정을 넘겨서도(브라우저 탭을 계속 켜둔 채로) 스스로 오늘자 상태로 갱신되도록,
  // attendanceToday도 환율/날씨처럼 1분마다 재조회한다. 새 날짜가 되면 서버가 빈 기록(null)을
  // 돌려주므로 출근/퇴근/외근/출장/복귀 버튼이 자동으로 새로 시작하는 상태로 보인다.
  useEffect(() => {
    if (permLoading || !hasDetail('home', 'checkin')) return;
    const timer = setInterval(() => {
      load('attendanceToday', async () => (await API.get('/hr/attendance/today')).data);
    }, 60 * 1000);
    return () => clearInterval(timer);
  }, [permLoading]); // eslint-disable-line

  // 홈 화면 근태체크 위젯 사용여부(인사관리 > 사원정보 > 옵션) 조회 — 행이 없으면 기본 사용.
  useEffect(() => {
    if (permLoading || !hasDetail('home', 'checkin')) return;
    API.get('/hr/menu-options')
      .then(r => {
        const off = (r.data || []).some(o => o.tab_key === 'home_attendance_widget' && !o.enabled);
        setAttWidgetEnabled(!off);
      })
      .catch(() => {});
  }, [permLoading]); // eslint-disable-line

  // 근태체크 버튼(출근/퇴근/외근/출장/복귀) — 홈 화면에서 바로 눌러 본인의 오늘자 근태관리 기록을
  // 남긴다. 응답에 포함된 6개 필드를 전부 반영해야 5개 버튼 모두 클릭 즉시(새로고침 없이) 방금 찍힌
  // 시간이 화면에 바로 나타난다 — field_time/trip_time/return_time을 누락하면 외근/출장/복귀는
  // 다음 1분 주기 재조회(위 useEffect) 전까지 이전 상태로 보이는 버그가 있었다.
  const checkAttendance = (type) => {
    setAttBusy(true);
    API.post('/hr/attendance/check', { type })
      .then(r => setData(d => ({ ...d, attendanceToday: {
        ...(d.attendanceToday || {}),
        check_in: r.data.check_in, check_out: r.data.check_out, status: r.data.status, note: r.data.note,
        field_time: r.data.field_time, trip_time: r.data.trip_time, return_time: r.data.return_time,
      } })))
      .catch(err => alert(err.response?.data?.message || '처리 중 오류가 발생했습니다.'))
      .finally(() => setAttBusy(false));
  };

  const fx = data.fx, wx = data.weather, social = data.social;

  const cityDivider = <div style={{ width: 1, alignSelf: 'stretch', background: '#f0ece1', flexShrink: 0 }} />;

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>
      {/* 회원가입 후 첫 로그인 안내 배너 — 상세정보(추가정보) 입력을 유도한다. 버튼을 누르면
          계정관리(마이페이지)로 이동해 "추가정보" 모달이 바로 열린다. */}
      {showFirstLoginBanner && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px',
          marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.6 }}>
            👋 <b>가입을 환영합니다!</b> 아직 입력하지 않은 상세정보(가족사항/학력/경력/건강사항 등)가 있다면 등록해주세요.
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => { setShowFirstLoginBanner(false); navigate('/mypage', { state: { openHrCard: true } }); }}
              style={{ padding: '7px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              📇 추가정보 입력
            </button>
            <button onClick={() => setShowFirstLoginBanner(false)}
              style={{ padding: '7px 12px', background: 'none', border: 'none', color: '#64748b', fontSize: 16, cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex', alignItems: 'stretch', marginBottom: 24, overflowX: 'auto' }}>
        <div style={{ padding: '20px 24px', flex: '1 1 320px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            {formatDateKR()} {user?.role === 'superadmin' ? '최고관리자' : user?.name}
            {data.myStatus && <StatusBadge status={data.myStatus} />}
          </h2>
          {/* 2026-08: 최고관리자(ninegro)는 인사관리 근태관리 목록/집계에서 이미 제외되는 계정(account_type=
              'admin_only')이라, 여기서 체크인해도 아무 목록에도 나타나지 않는 고아 기록만 쌓인다.
              애초에 실제 근로자가 아니므로 홈 화면 근태체크 위젯 자체를 노출하지 않는다. */}
          {attWidgetEnabled && hasDetail('home', 'checkin') && user?.role !== 'superadmin' && (
            <AttendanceCheckWidget today={data.attendanceToday} loading={!loaded.attendanceToday} busy={attBusy} onCheck={checkAttendance} />
          )}
        </div>
        {cityDivider}
        <div style={{ padding: '20px 24px', flex: '1 1 280px' }}>
          <ContentUploadWidget social={social} loading={!loaded.social}
            onOpenStats={() => setShowSocialStats(true)}
            onSaveManual={(channel, count) => saveManualSocial(channel, count)} />
        </div>
        {cityDivider}
        <div style={{ padding: '20px 24px', flexShrink: 0 }}>
          <ExchangeRateWidget fx={fx} loading={!loaded.fx} refreshing={fxRefreshing} onRefresh={refreshFx} />
        </div>
        {cityDivider}
        <div style={{ padding: '20px 24px', flexShrink: 0 }}>
          <WeatherWidget wx={wx} loading={!loaded.weather} cityLabel={cityOverride?.name} onPick={() => setShowCityPicker(true)} />
        </div>
      </div>
      {showCityPicker && (
        <CityPickerModal current={cityOverride} onSelect={selectCity} onClose={() => setShowCityPicker(false)} />
      )}
      {showSocialStats && (
        <SocialStatsModal onClose={() => setShowSocialStats(false)} />
      )}

      {/* 오늘 진행현황 요약 타일(신규, 한 줄) — 주요일정/업무관리/결재요청/영업관리/출고관리/생산관리를
          로그인 당일 기준 진행중 건수로 압축해서 보여준다. 클릭하면 상세보기 모달 + "전체보기" 버튼. */}
      {(() => {
        const tiles = [];
        if (canRead('schedules')) tiles.push({ key: 'schedules', icon: '📅', title: '주요일정', color: '#C99A4B', to: '/schedules', items: data.schedules });
        if (canRead('tasks')) tiles.push({ key: 'tasks', icon: '🗂️', title: '업무관리', color: '#C1553A', to: '/tasks', items: data.tasks });
        if (canRead('productions') || canRead('sales')) tiles.push({ key: 'approvals', icon: '📝', title: '결재요청', color: '#D98B4E', to: '/productions', items: data.approvals });
        if (canRead('sales-activities')) tiles.push({ key: 'salesActivities', icon: '🤝', title: '영업관리', color: '#5B7FBD', to: '/sales-activities', items: data.salesActivities });
        if (canRead('sales')) tiles.push({ key: 'sales', icon: '🚚', title: '출고관리', color: '#3E4A55', to: '/sales', items: data.sales });
        if (canRead('productions')) tiles.push({ key: 'productions', icon: '🏭', title: '생산관리', color: '#8B8358', to: '/productions', items: data.productions });
        if (!tiles.length) return null;
        return (
          <div style={{ display: 'flex', gap: 18, marginTop: 6, marginBottom: 30, flexWrap: 'wrap' }}>
            {tiles.map(t => (
              <SummaryTile key={t.key} icon={t.icon} title={t.title} color={t.color}
                count={(t.items || []).length} loading={!loaded[t.key]}
                onClick={() => setOpenTile(t)} />
            ))}
          </div>
        );
      })()}
      {openTile && (
        <TileDetailModal tile={openTile}
          onNavigate={() => { const to = openTile.to; setOpenTile(null); navigate(to); }}
          onClose={() => setOpenTile(null)} />
      )}

      {/* 한 줄에 3개씩 고정 배치 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
        {canRead('notices') && (
          <DashCard to="/notices" icon="📢" title="공지사항" color="#4F7A63"
            loading={!loaded.notices} items={data.notices?.slice(0, ROW_COUNT)} empty="등록된 공지사항이 없습니다."
            onItemClick={it => setOpenCardItem({ cardType: 'notices', item: it, to: '/notices' })} />
        )}
        {canRead('schedules') && (
          <DashCard to="/schedules" icon="📅" title="오늘의 주요일정" color="#C99A4B"
            loading={!loaded.schedules} items={data.schedules?.slice(0, ROW_COUNT)} empty="오늘 예정된 일정이 없습니다."
            onItemClick={it => setOpenCardItem({ cardType: 'schedules', item: it, to: '/schedules' })} />
        )}
        {(canRead('productions') || canRead('sales')) && (
          <DashCard to="/productions" icon="📝" title="결재요청" color="#D98B4E"
            loading={!loaded.approvals} items={data.approvals?.slice(0, ROW_COUNT)} empty="대기중인 결재요청이 없습니다."
            onItemClick={it => setOpenCardItem({ cardType: 'approvals', item: it, to: '/productions' })} />
        )}
        {canRead('tasks') && (
          <DashCard to="/tasks" icon="🗂️" title="업무관리" color="#C1553A"
            loading={!loaded.tasks} items={data.tasks?.slice(0, ROW_COUNT)} empty="진행중인 업무가 없습니다."
            onItemClick={it => setOpenCardItem({ cardType: 'tasks', item: it, to: '/tasks' })} />
        )}
        {canRead('sales') && (
          <DashCard to="/sales" icon="🚚" title="출고관리" color="#3E4A55"
            loading={!loaded.sales} items={data.sales?.slice(0, ROW_COUNT)} empty="등록된 납품출고가 없습니다."
            onItemClick={it => setOpenCardItem({ cardType: 'sales', item: it, to: '/sales' })} />
        )}
        {canRead('productions') && (
          <DashCard to="/productions" icon="🏭" title="생산관리" color="#8B8358"
            loading={!loaded.productions} items={data.productions?.slice(0, ROW_COUNT)} empty="생산중인 항목이 없습니다."
            onItemClick={it => setOpenCardItem({ cardType: 'productions', item: it, to: '/productions' })} />
        )}
      </div>
      {openCardItem && (
        <CardItemDetailModal cardType={openCardItem.cardType} item={openCardItem.item}
          onClose={() => setOpenCardItem(null)}
          onNavigate={() => { const to = openCardItem.to; setOpenCardItem(null); navigate(to); }} />
      )}
    </div>
  );
}
